import React, { FC } from 'react';
import { Button, CardMedia, Container, Grid, makeStyles, Theme, Typography, useMediaQuery } from '@material-ui/core';
import Link from 'next/link';
import { SpacingLayout } from 'components/atoms';
import { animated } from 'react-spring';

interface TextImageSectionProps {
  title: { highlightWord?: string; text: string };
  underTitleLineShow?: boolean;
  text: string;
  linkBtn: { text: string; link: string };
  image: string;
  align?: 'left' | 'right';
}

enum Position {
  left = 'row',
  right = 'row-reverse',
}

const useStyles = makeStyles((theme: Theme) => ({
  cardMedia: {
    maxHeight: 250,
    [theme.breakpoints.up('md')]: {
      maxHeight: 395,
    },
  },
  title: {
    textTransform: 'uppercase',
    marginBottom: theme.spacing(2),
    '& span': {
      color: theme.palette.primary.main,
    },
    [theme.breakpoints.only('sm')]: {
      fontSize: 24,
    },
    [theme.breakpoints.up('lg')]: {
      marginBottom: theme.spacing(3),
    },
  },
  underLine: {
    margin: theme.spacing(0, 0, 2, -2),
    borderTop: `2px solid ${theme.palette.primary.main}`,
    maxWidth: 193,
    [theme.breakpoints.up('sm')]: {
      marginBottom: theme.spacing(3),
      maxWidth: 240,
    },
  },
  text: {
    marginBottom: theme.spacing(6),
    [theme.breakpoints.up('sm')]: {
      fontSize: 18,
    },
    [theme.breakpoints.up('lg')]: {
      marginBottom: theme.spacing(8),
      fontSize: 24,
      maxWidth: 600,
    },
  },
}));

const TextImageSection: FC<TextImageSectionProps> = ({ text, underTitleLineShow, title, linkBtn, image, align = 'left' }) => {
  const classes = useStyles();
  const isMediumUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  return (
    <SpacingLayout>
      <Container maxWidth={false} fixed>
        <Grid container spacing={6} justify="center" direction={Position[align]} alignItems="center">
          <Grid item md={6}>
            <animated.div data-aos="fade-down" data-aos-delay="400" id="text-image">
              <CardMedia className={classes.cardMedia} component="img" alt={image} image={image} />
            </animated.div>
          </Grid>
          <Grid item md={6}>
            <animated.div
              data-aos="fade-up"
              data-aos-delay="400"
              data-aos-anchor={isMediumUp ? '#text-image' : null}
              data-aos-anchor-placement="top-bottom"
            >
              <Typography className={classes.title} variant="h2" color={'textPrimary'}>
                {!!title.highlightWord && <span>{title.highlightWord}</span>} {title.text}
              </Typography>
              {!!underTitleLineShow && <hr className={classes.underLine} />}
              <Typography className={classes.text} variant="h4" component={'h3'} color={'textPrimary'}>
                {text}
              </Typography>
              <Link href={linkBtn.link}>
                <a>
                  <Button color="primary" variant="contained">
                    {linkBtn.text}
                  </Button>
                </a>
              </Link>
            </animated.div>
          </Grid>
        </Grid>
      </Container>
    </SpacingLayout>
  );
};

export default TextImageSection;
